import { Avatar, Badge, Caption1, Input, Spinner, Tooltip, makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import {
  ArrowUpload16Regular,
  Camera16Regular,
  Checkmark12Filled,
  Dismiss16Regular,
  Globe16Regular,
  Key16Regular,
  Laptop16Regular,
  Person16Regular,
  ShieldCheckmark16Regular,
} from "@fluentui/react-icons";
import { useEffect, useRef, useState } from "react";
import { AppDialog, ConfirmDialog, DialogActionsBar, DialogField, DialogGrid2 } from "@/components/app-dialog";
import { FuiButton } from "@/components/fui-control";
import { formatDate } from "@/lib/date-format";
import { useSettingsHub } from "@/shell/settings-hub";
import { useCatalogStore } from "@/store/catalog-store";
import { deviceKindLabel, getDevices, signOutDevice, signOutOtherDevices, useDevices, type DeviceKind } from "./devices";
import { MockCaseMenu, type MockCase } from "./MockCaseMenu";
import { AvatarCropDialog } from "./AvatarCropDialog";
import { rejectAvatarEdge, rejectAvatarFile } from "./avatar-crop";
import { signOut, useSession } from "./session";

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    minWidth: 0,
    width: "100%",
  },
  card: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    padding: "20px",
    borderRadius: tokens.borderRadiusLarge,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  cardHead: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
  cardTitle: {
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground1,
  },
  cardSubtitle: {
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    color: tokens.colorNeutralForeground3,
  },
  divider: {
    height: "1px",
    backgroundColor: tokens.colorNeutralStroke2,
  },
  profileRow: {
    display: "flex",
    alignItems: "center",
    gap: "20px",
    minWidth: 0,
  },
  avatarActions: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "6px",
    minWidth: 0,
    flex: 1,
  },
  avatarButtons: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "8px",
  },
  avatarHint: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  errorText: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorPaletteRedForeground1,
    marginTop: "2px",
  },
  formGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "14px",
    minWidth: 0,
  },
  emailCard: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    padding: "12px 16px",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  emailBadgeRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  emailIcon: {
    color: tokens.colorNeutralForeground2,
    flexShrink: 0,
  },
  emailAddress: {
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground1,
  },
  emailHint: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  actionsBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    paddingTop: "16px",
    borderTopWidth: "1px",
    borderTopStyle: "solid",
    borderTopColor: tokens.colorNeutralStroke2,
  },
  actionStatus: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  actionSavedCheck: {
    color: tokens.colorPaletteGreenForeground1,
  },
  actionButtonGroup: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginLeft: "auto",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    minWidth: 0,
  },
  saveRow: {
    display: "flex",
    justifyContent: "flex-end",
  },
  headCard: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalM,
    minWidth: 0,
    paddingBottom: "16px",
    marginBottom: "4px",
    borderBottomWidth: "1px",
    borderBottomStyle: "solid",
    borderBottomColor: tokens.colorNeutralStroke2,
  },
  face: {
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "72px",
    height: "72px",
    flexShrink: 0,
    overflow: "hidden",
    boxSizing: "border-box",
    padding: 0,
    borderRadius: tokens.borderRadiusCircular,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase500,
    lineHeight: "1",
    cursor: "pointer",
    ":hover": { backgroundColor: tokens.colorNeutralBackground1Hover },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "2px",
    },
    ":focus-visible [data-face-cam]": { opacity: 1 },
    ":active": { backgroundColor: tokens.colorNeutralBackground1Pressed },
    ":disabled": { opacity: 0.5, cursor: "default" },
    "& .fui-Avatar": {
      backgroundColor: tokens.colorNeutralBackground1,
      color: tokens.colorNeutralForeground3,
    },
  },
  faceError: {
    border: `1px solid ${tokens.colorPaletteRedBorder2}`,
  },
  photo: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    borderRadius: tokens.borderRadiusCircular,
  },
  faceOverlay: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: "rgba(0, 0, 0, 0.44)",
    color: tokens.colorNeutralForegroundOnBrand,
    opacity: 0,
    transition: "opacity 120ms",
    ":hover": { opacity: 1 },
  },
  spin: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: "rgba(255,255,255,0.72)",
  },
  faceCopy: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "4px",
    minWidth: 0,
  },
  faceActions: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
  },
  email: {
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: tokens.colorNeutralForeground3,
  },
  quiet: { color: tokens.colorNeutralForeground3 },
  error: { color: tokens.colorPaletteRedForeground1 },
  file: { display: "none" },
  security: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    minWidth: 0,
    width: "100%",
  },
  securityRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
    padding: "12px 16px",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  securityRowLeft: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    minWidth: 0,
  },
  securityIconWrap: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "32px",
    height: "32px",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground2,
    flexShrink: 0,
    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.04)",
  },
  securityRowText: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    minWidth: 0,
  },
  securityRowTitle: {
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground1,
  },
  deviceList: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  deviceCard: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    padding: "14px 16px",
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  deviceCardCurrent: {
    border: `1px solid ${tokens.colorBrandStroke1}`,
    backgroundColor: tokens.colorNeutralBackground1Selected,
  },
  deviceHeaderRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexWrap: "wrap",
  },
  deviceIconLarge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "36px",
    height: "36px",
    flexShrink: 0,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground1,
  },
  group: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXXS,
    minWidth: 0,
  },
  groupBody: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },
  deviceRow: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    minWidth: 0,
    padding: `${tokens.spacingVerticalS} 0`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    ":last-child": { borderBottom: "none" },
  },
  deviceIcon: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "28px",
    height: "28px",
    flexShrink: 0,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground2,
  },
  deviceText: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    flex: 1,
  },
  clip: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  deviceActions: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
    flexShrink: 0,
  },
});

type Draft = {
  name: string;
  title: string;
  affiliation: string;
  email: string;
  avatar: string;
  error: string;
  busy: boolean;
};

const CASES: MockCase[] = [
  { id: "initials", label: "이니셜만" },
  { id: "photo", label: "프로필 사진" },
  { id: "email", label: "이메일이 있음" },
  { id: "long", label: "긴 이름" },
  { id: "blank", label: "이름이 비어 저장할 수 없음" },
  { id: "bad", label: "올릴 수 없는 형식" },
  { id: "busy", label: "사진을 올리는 중" },
];

function samplePortrait(): string {
  const canvas = document.createElement("canvas");
  canvas.width = 96;
  canvas.height = 96;
  const ctx = canvas.getContext("2d");
  if (!ctx) return "";
  ctx.fillStyle = "#d6d6d6";
  ctx.fillRect(0, 0, 96, 96);
  ctx.fillStyle = "#5c5c5c";
  ctx.beginPath();
  ctx.arc(48, 36, 14, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse(48, 88, 28, 24, 0, Math.PI, 0);
  ctx.fill();
  return canvas.toDataURL("image/png");
}

function caseDraft(id: string): Draft {
  const empty: Draft = { name: "나", title: "", affiliation: "", email: "", avatar: "", error: "", busy: false };
  if (id === "photo") return { ...empty, name: "김민수", title: "기획", affiliation: "웨이브엑스", avatar: samplePortrait() };
  if (id === "email") return { ...empty, name: "이나", email: "lee@wavex.example" };
  if (id === "long") return { ...empty, name: "이름이아주길어져서한줄에담기어려운표시이름입니다".slice(0, 40), title: "수석 컨설턴트", affiliation: "웨이브엑스 SI" };
  if (id === "blank") return { ...empty, name: "" };
  if (id === "bad") return { ...empty, error: "JPEG, PNG, WebP만 올릴 수 있습니다" };
  if (id === "busy") return { ...empty, busy: true };
  return empty;
}

/** 프로필. 저장하면 표시 이름·직함·소속이 바뀐다. 사진은 크롭 화면에서 정사각으로 자른 뒤 이 브라우저에 둔다. */
export function AccountSettingsDialog({ open, onClose, embedded = false }: { open: boolean; onClose: () => void; embedded?: boolean }) {
  const styles = useStyles();
  const profile = useCatalogStore((s) => s.profile);
  const setProfile = useCatalogStore((s) => s.setProfile);
  const fileRef = useRef<HTMLInputElement>(null);
  const session = useSession();
  const [name, setName] = useState(profile.displayName);
  const [title, setTitle] = useState(profile.title ?? "");
  const [affiliation, setAffiliation] = useState(profile.affiliation ?? "");
  const [email, setEmail] = useState(profile.email ?? "");
  const [avatar, setAvatar] = useState(profile.avatar ?? "");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [crop, setCrop] = useState<{ url: string; width: number; height: number } | null>(null);
  const [savedNote, setSavedNote] = useState(false);
  const [leaveConfirm, setLeaveConfirm] = useState<(() => void) | null>(null);

  useEffect(() => {
    if (!crop) return;
    const url = crop.url;
    return () => URL.revokeObjectURL(url);
  }, [crop]);

  useEffect(() => {
    if (open) return;
    setCrop(null);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setName(profile.displayName);
    setTitle(profile.title ?? "");
    setAffiliation(profile.affiliation ?? "");
    setEmail(profile.email ?? "");
    setAvatar(profile.avatar ?? "");
    setError("");
    setBusy(false);
    setSavedNote(false);
  }, [open, profile.displayName, profile.title, profile.affiliation, profile.email, profile.avatar]);

  const dirty =
    name.trim() !== profile.displayName ||
    title.trim() !== (profile.title ?? "") ||
    affiliation.trim() !== (profile.affiliation ?? "") ||
    avatar !== (profile.avatar ?? "");

  const trimmed = name.trim();
  const nameInvalid = trimmed.length === 0;
  const canSave = !busy && dirty && !nameInvalid && trimmed.length <= 40 && title.trim().length <= 40 && affiliation.trim().length <= 40;

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    if (fileRef.current) fileRef.current.value = "";
    const reason = rejectAvatarFile(file);
    if (reason) {
      setError(reason);
      return;
    }
    setBusy(true);
    setError("");
    try {
      const bitmap = await createImageBitmap(file);
      const width = bitmap.width;
      const height = bitmap.height;
      bitmap.close();
      const edge = rejectAvatarEdge(width, height);
      if (edge) {
        setError(edge);
        return;
      }
      setCrop({ url: URL.createObjectURL(file), width, height });
    } catch {
      setError("이 브라우저에서는 사진을 열 수 없습니다");
    } finally {
      setBusy(false);
    }
  };

  const emailText = email || session?.email || "로그인하면 여기에 표시됩니다";

  const saveProfile = () => {
    if (!canSave && embedded) return;
    setProfile({
      displayName: trimmed,
      title: title.trim(),
      affiliation: affiliation.trim(),
      email: email.trim(),
      avatar,
    });
    setSavedNote(true);
    if (!embedded) onClose();
  };

  const resetProfile = () => {
    setName(profile.displayName);
    setTitle(profile.title ?? "");
    setAffiliation(profile.affiliation ?? "");
    setEmail(profile.email ?? "");
    setAvatar(profile.avatar ?? "");
    setError("");
    setSavedNote(false);
  };

  useEffect(() => {
    if (!embedded) return;
    useSettingsHub.getState().setCloseGuard(() => {
      if (dirty) {
        setLeaveConfirm(() => () => useSettingsHub.getState().close());
        return false;
      }
      return true;
    });
    useSettingsHub.getState().setSectionGuard((next) => {
      if (dirty && next !== "account") {
        setLeaveConfirm(() => () => useSettingsHub.getState().commitSection(next));
        return false;
      }
      return true;
    });
    return () => {
      useSettingsHub.getState().setCloseGuard(null);
      useSettingsHub.getState().setSectionGuard(null);
    };
  }, [embedded, dirty]);

  const apply = (draft: Draft) => {
    setName(draft.name);
    setTitle(draft.title);
    setAffiliation(draft.affiliation);
    setEmail(draft.email);
    setAvatar(draft.avatar);
    setError(draft.error);
    setBusy(draft.busy);
    setSavedNote(false);
  };

  const pickCase = (id: string) => {
    apply(caseDraft(id));
  };

  const fields = (
    <div className={styles.container}>
      {/* 1. 프로필 기본 정보 카드 */}
      <div className={styles.card}>
        <div className={styles.cardHead}>
          <div className={styles.cardTitle}>기본 정보</div>
          <Caption1 className={styles.cardSubtitle}>
            팀원들에게 표시되는 프로필 사진과 이름, 직함, 소속을 관리합니다.
          </Caption1>
        </div>

        {/* 사진 업로드 영역 */}
        <div className={styles.profileRow}>
          <button
            type="button"
            className={mergeClasses(styles.face, error && styles.faceError)}
            aria-label={avatar ? "프로필 사진 바꾸기" : "사진 올리기"}
            disabled={busy}
            onClick={() => fileRef.current?.click()}
          >
            {avatar ? <img className={styles.photo} src={avatar} alt="" /> : <Avatar size={72} color="neutral" aria-hidden />}
            <span className={styles.faceOverlay} data-face-cam="">
              <Camera16Regular aria-hidden />
            </span>
            {busy ? (
              <span className={styles.spin}>
                <Spinner size="tiny" aria-label="사진을 올리고 있습니다" />
              </span>
            ) : null}
          </button>

          <div className={styles.avatarActions}>
            <div className={styles.avatarButtons}>
              <FuiButton
                appearance="outline"
                density="dialog"
                disabled={busy}
                icon={<ArrowUpload16Regular />}
                onClick={() => fileRef.current?.click()}
              >
                {avatar ? "사진 바꾸기" : "사진 올리기"}
              </FuiButton>
              {avatar ? (
                <FuiButton
                  appearance="subtle"
                  density="dialog"
                  tone="danger"
                  disabled={busy}
                  icon={<Dismiss16Regular />}
                  onClick={() => {
                    setAvatar("");
                    setError("");
                    setSavedNote(false);
                  }}
                >
                  사진 삭제
                </FuiButton>
              ) : null}
            </div>
            <Caption1 className={styles.avatarHint}>
              권장 규격: 정사각 128×128px 이상 (JPEG, PNG, WebP)
            </Caption1>
            {error ? (
              <Caption1 className={styles.errorText} role="alert">
                {error}
              </Caption1>
            ) : null}
          </div>
        </div>

        <input
          ref={fileRef}
          className={styles.file}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          aria-label="사진 파일"
          onChange={(event) => void onFile(event.target.files?.[0])}
        />

        <div className={styles.divider} />

        {/* 폼 필드: 표시 이름 & 직함 & 소속 */}
        <div className={styles.formGroup}>
          <DialogField label="표시 이름" required>
            <Input
              value={name}
              maxLength={40}
              disabled={busy}
              placeholder="이름을 입력하세요"
              aria-invalid={nameInvalid}
              onChange={(_, data) => {
                setName(data.value);
                setSavedNote(false);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && canSave) {
                  e.preventDefault();
                  saveProfile();
                }
              }}
            />
            {nameInvalid && dirty ? (
              <Caption1 className={styles.errorText} role="alert">
                표시 이름을 입력하세요
              </Caption1>
            ) : null}
          </DialogField>

          <DialogGrid2>
            <DialogField label="직함">
              <Input
                value={title}
                maxLength={40}
                disabled={busy}
                placeholder="예: 수석 프로덕트 매니저"
                onChange={(_, data) => {
                  setTitle(data.value);
                  setSavedNote(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && canSave) {
                    e.preventDefault();
                    saveProfile();
                  }
                }}
              />
            </DialogField>
            <DialogField label="소속">
              <Input
                value={affiliation}
                maxLength={40}
                disabled={busy}
                placeholder="예: 웨이브엑스 코어팀"
                onChange={(_, data) => {
                  setAffiliation(data.value);
                  setSavedNote(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && canSave) {
                    e.preventDefault();
                    saveProfile();
                  }
                }}
              />
            </DialogField>
          </DialogGrid2>
        </div>
      </div>

      {/* 2. 계정 이메일 카드 */}
      <div className={styles.card}>
        <div className={styles.cardHead}>
          <div className={styles.cardTitle}>계정 이메일</div>
          <Caption1 className={styles.cardSubtitle}>
            로그인 및 알림 수신에 사용되는 고유 계정입니다.
          </Caption1>
        </div>

        <div className={styles.emailCard}>
          <div className={styles.emailBadgeRow}>
            <Person16Regular className={styles.emailIcon} />
            <span className={styles.emailAddress}>{emailText}</span>
            <Badge size="medium" appearance="tint" color="informative">
              인증된 계정
            </Badge>
          </div>
          <Caption1 className={styles.emailHint}>
            비밀번호 변경 및 기기 로그인 세션 관리는 왼쪽 메뉴의 [보안] 탭에서 가능합니다.
          </Caption1>
        </div>
      </div>

      {/* 3. 하단 액션 바 (embedded 일 때) */}
      {embedded ? (
        <div className={styles.actionsBar}>
          <div className={styles.actionStatus}>
            {savedNote && !dirty ? (
              <>
                <Checkmark12Filled className={styles.actionSavedCheck} />
                <span>변경사항이 저장되었습니다.</span>
              </>
            ) : null}
          </div>
          <div className={styles.actionButtonGroup}>
            <FuiButton
              appearance="secondary"
              density="dialog"
              disabled={!dirty || busy}
              onClick={resetProfile}
            >
              취소
            </FuiButton>
            <FuiButton
              appearance="primary"
              density="dialog"
              disabled={!canSave || busy}
              onClick={saveProfile}
            >
              변경사항 저장
            </FuiButton>
          </div>
        </div>
      ) : null}

      <ConfirmDialog
        open={leaveConfirm != null}
        title="변경사항을 버릴까요?"
        message="저장하지 않은 프로필 정보가 사라집니다."
        confirmLabel="버리기"
        cancelLabel="머무르기"
        onCancel={() => setLeaveConfirm(null)}
        onConfirm={() => {
          resetProfile();
          const action = leaveConfirm;
          setLeaveConfirm(null);
          action?.();
        }}
      />
    </div>
  );

  return (
    <>
      {embedded ? (
        fields
      ) : (
        <AppDialog
          open={open}
          onClose={onClose}
          title="계정 설정"
          width="sm"
          className="max-h-[calc(100dvh-48px)]"
          persistWhile={crop != null}
          titleAside={<MockCaseMenu cases={CASES} onPick={pickCase} />}
          footer={
            <DialogActionsBar
              cancelLabel="취소"
              confirmLabel="저장"
              disabled={!canSave}
              onCancel={onClose}
              onConfirm={saveProfile}
            />
          }
        >
          {fields}
        </AppDialog>
      )}
      <AvatarCropDialog
        source={crop}
        onCancel={() => setCrop(null)}
        onApply={(next) => {
          setAvatar(next);
          setError("");
          setCrop(null);
        }}
      />
    </>
  );
}

function DeviceGlyph({ kind }: { kind: DeviceKind }) {
  return kind === "desktop" ? <Laptop16Regular /> : <Globe16Regular />;
}

/** 보안. 이 브라우저 로그아웃은 세션을 끝내고, 다른 기기는 그 기기만 로그아웃으로 표시한다. */
export function AccountSecuritySettings({ onSignedOut }: { onSignedOut?: () => void }) {
  const styles = useStyles();
  useDevices();
  const rows = getDevices();
  const [pending, setPending] = useState<string | "others" | "self" | null>(null);
  const self = pending === "self";
  const others = pending === "others";
  const canSignOutOthers = rows.some((row) => !row.current && !row.signedOut);

  return (
    <div className={styles.security}>
      {/* 1. 인증 및 계정 보안 카드 */}
      <div className={styles.card}>
        <div className={styles.cardHead}>
          <div className={styles.cardTitle}>인증 및 계정 보안</div>
          <Caption1 className={styles.cardSubtitle}>
            로그인 방식 및 계정 보호를 위한 2단계 인증 설정입니다.
          </Caption1>
        </div>

        <div className={styles.securityRow}>
          <div className={styles.securityRowLeft}>
            <div className={styles.securityIconWrap}>
              <ShieldCheckmark16Regular />
            </div>
            <div className={styles.securityRowText}>
              <div className={styles.securityRowTitle}>2단계 인증 (2FA)</div>
              <Caption1 className={styles.quiet}>
                새로운 기기나 위치에서 로그인할 때 일회용 인증 코드로 계정을 안전하게 보호합니다.
              </Caption1>
            </div>
          </div>
          <Badge size="medium" appearance="tint" color="informative">
            활성화됨
          </Badge>
        </div>

        <div className={styles.securityRow}>
          <div className={styles.securityRowLeft}>
            <div className={styles.securityIconWrap}>
              <Key16Regular />
            </div>
            <div className={styles.securityRowText}>
              <div className={styles.securityRowTitle}>로그인 비밀번호</div>
              <Caption1 className={styles.quiet}>
                통합 인증(SSO) 및 단일 로그인(SSO) 세션으로 안전하게 관리되고 있습니다.
              </Caption1>
            </div>
          </div>
          <Badge size="medium" appearance="tint" color="informative">
            보안 유지 중
          </Badge>
        </div>
      </div>

      {/* 2. 로그인된 기기 및 활성 세션 카드 */}
      <div className={styles.card}>
        <div className={styles.cardHead}>
          <div className={styles.cardTitle}>로그인된 기기</div>
          <Caption1 className={styles.cardSubtitle}>
            현재 계정에 로그인되어 있는 웹 브라우저 및 애플리케이션 세션 목록입니다.
          </Caption1>
        </div>

        <div className={styles.deviceList}>
          {rows.map((row) => (
            <div
              key={row.id}
              className={mergeClasses(styles.deviceCard, row.current && styles.deviceCardCurrent)}
            >
              <div className={styles.securityRowLeft}>
                <span className={styles.deviceIconLarge} aria-hidden>
                  <DeviceGlyph kind={row.kind} />
                </span>
                <div className={styles.deviceText}>
                  <div className={styles.deviceHeaderRow}>
                    <span className={styles.clip}>{row.name}</span>
                    {row.current ? (
                      <Badge size="small" appearance="tint" color="success">
                        현재 접속 중
                      </Badge>
                    ) : null}
                  </div>
                  <Caption1 className={styles.quiet}>
                    {deviceKindLabel(row.kind)} · 마지막 활동: {formatDate(row.lastUsedAt, "dateTime")}
                  </Caption1>
                </div>
              </div>

              <div>
                {row.current ? (
                  <FuiButton appearance="secondary" density="dialog" onClick={() => setPending("self")}>
                    로그아웃
                  </FuiButton>
                ) : row.signedOut ? (
                  <Caption1 className={styles.quiet}>로그아웃됨</Caption1>
                ) : (
                  <FuiButton appearance="subtle" density="dialog" onClick={() => setPending(row.id)}>
                    세션 종료
                  </FuiButton>
                )}
              </div>
            </div>
          ))}
        </div>

        {canSignOutOthers ? (
          <div style={{ paddingTop: "8px", display: "flex", justifyContent: "flex-end" }}>
            <FuiButton appearance="secondary" density="dialog" onClick={() => setPending("others")}>
              다른 모든 기기에서 로그아웃
            </FuiButton>
          </div>
        ) : null}
      </div>

      <ConfirmDialog
        open={pending !== null}
        title={self ? "로그아웃" : others ? "다른 기기에서 로그아웃" : "기기에서 로그아웃"}
        message={self ? "이 브라우저의 로그인을 끝냅니다. 로컬 플랜은 그대로 둡니다." : "그 기기는 다음부터 다시 로그인해야 합니다. 이 브라우저는 그대로입니다."}
        confirmLabel="로그아웃"
        onCancel={() => setPending(null)}
        onConfirm={() => {
          if (self) {
            signOut();
            setPending(null);
            onSignedOut?.();
            return;
          }
          if (others) signOutOtherDevices();
          else if (pending) signOutDevice(pending);
          setPending(null);
        }}
      />
    </div>
  );
}